import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { HiOutlineChatBubbleLeftRight, HiOutlineSparkles, HiOutlineCalendarDays } from "react-icons/hi2";
import Button from '../../../components/button/button';
import Switch from "../../../components/switch";
import ConfirmDialog from "../../../components/confirm-dialog/confirm-dialog";
import {
    getDashboardResponse,
    cancelSubscription,
    resumeSubscription,
    updateAddons
} from '../../../services/billing';
import { formatCents } from "../plan-features";
import { SUBSCRIPTION_BASE, SectionCard, Pill, Notice, LoadingState, UsageBar, EmptyState } from "../components";

const STATUS_PILLS = {
    active: { tone: 'success', text: 'Active' },
    trialing: { tone: 'info', text: 'Trial' },
    past_due: { tone: 'warning', text: 'Payment overdue' },
    unpaid: { tone: 'danger', text: 'Unpaid' },
    incomplete: { tone: 'warning', text: 'Payment incomplete' },
};

function PlanSummary({ dashboardData, onChangePlan, onCancel, onKeepPlan, busy }) {
    const { plan, currentPeriod, pendingChange, legacyPlan } = dashboardData;
    const status = STATUS_PILLS[dashboardData.status];
    const isFree = !legacyPlan && plan.priceMonthlyCents === 0;

    return (
        <SectionCard bodyClassName="flex h-full flex-col gap-5 p-5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Current plan</p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">{plan.displayName}</p>
                </div>
                {status && <Pill tone={status.tone}>{status.text}</Pill>}
            </div>

            <p className="text-gray-900">
                <span className="text-3xl font-semibold tracking-tight">{plan.price || formatCents(plan.priceMonthlyCents || 0)}</span>
                <span className="ml-1 text-sm text-gray-500">/ month</span>
            </p>

            <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiOutlineCalendarDays className="h-4 w-4 text-gray-400" />
                <span>{currentPeriod.start} – {currentPeriod.end}</span>
            </div>

            <div className="mt-auto flex flex-wrap gap-2">
                {pendingChange
                    ? <Button text="Keep current plan" onClick={onKeepPlan} loading={busy} disabled={busy} />
                    : <Button text={isFree || legacyPlan ? 'Upgrade plan' : 'Change plan'} onClick={onChangePlan} />}
                {!pendingChange && !isFree && !legacyPlan &&
                    <Button text="Cancel subscription" color="default" onClick={onCancel} disabled={busy} />}
            </div>
        </SectionCard>
    );
}

function UsageMeter({ icon: Icon, title, used, included, ratio, footer, alert, badge }) {
    return (
        <SectionCard bodyClassName="flex h-full flex-col gap-3 p-5">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                        <Icon className="h-4 w-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">{title}</p>
                </div>
                {badge}
            </div>
            <p className="text-gray-900">
                <span className="text-3xl font-semibold tracking-tight">{used}</span>
                {included != null && <span className="ml-1 text-sm text-gray-500">of {included}</span>}
            </p>
            <UsageBar ratio={ratio} />
            <div className="mt-auto space-y-1">
                {footer && <p className="text-xs text-gray-500">{footer}</p>}
                {alert && <p className="text-xs font-medium text-red-600 dark:text-red-400">{alert}</p>}
            </div>
        </SectionCard>
    );
}

function ConversationsMeter({ dashboardData }) {
    const { plan } = dashboardData;
    const conversations = dashboardData.usage.conversations;
    // the cap in force: a per-account limit (customLimit) or the plan's hard cap; else the plan's included amount
    const allowance = conversations.limit != null ? conversations.limit : conversations.included;

    let footer = null;
    if (conversations.customLimit) {
        footer = 'Custom limit for your account';
    } else if (conversations.hardCap) {
        footer = 'Hard limit on this plan';
    } else if (conversations.overage > 0) {
        footer = `${conversations.overage.toLocaleString('en-US')} extra conversations: ${conversations.overageAmount} (${conversations.overagePer100} per 100)`;
    } else if (conversations.overagePer100) {
        footer = `Then ${conversations.overagePer100} per 100 conversations`;
    }

    let alert = null;
    if (conversations.capReached) {
        alert = conversations.customLimit
            ? 'Account limit reached. Upgrade your plan to continue serving conversations.'
            : `${plan.displayName} plan limit reached. Upgrade to continue serving conversations.`;
    }

    return (
        <UsageMeter
            icon={HiOutlineChatBubbleLeftRight}
            title="Conversations"
            used={conversations.used.toLocaleString('en-US')}
            included={allowance != null ? allowance.toLocaleString('en-US') : null}
            ratio={allowance ? conversations.used / allowance : null}
            footer={footer}
            badge={conversations.capReached ? <Pill tone="danger">Limit reached</Pill> : null}
            alert={alert}
        />
    );
}

function AiUsageMeter({ dashboardData }) {
    const { plan, currentPeriod } = dashboardData;
    const ai = dashboardData.usage.aiUsage;
    const common = { icon: HiOutlineSparkles, title: 'AI usage' };
    const capBadge = ai.capReached ? <Pill tone="danger">Limit reached</Pill> : null;
    const capAlert = ai.capReached
        ? (ai.customCap
            ? `AI copilot is paused until ${currentPeriod.end} or an upgrade. Your chats keep running.`
            : `AI copilot is paused until ${currentPeriod.end} or an upgrade. Your chats keep running.`)
        : null;

    if (ai.byok) {
        return <UsageMeter {...common} used="Own key" badge={<Pill tone="accent" icon={false}>BYOK</Pill>}
                           footer="AI usage is billed by your AI provider" />;
    }
    if (ai.capUsd && (ai.covered || ai.customCap)) {
        return <UsageMeter
            {...common}
            used={ai.used}
            included={ai.cap}
            ratio={ai.usedUsd / ai.capUsd}
            footer={ai.customCap
                ? (ai.covered ? 'Covered by BotCircuits up to your account limit' : `Custom limit for your account; ${ai.included} included`)
                : 'Covered by BotCircuits up to the monthly limit'}
            badge={capBadge}
            alert={capAlert}
        />;
    }
    if (ai.covered) {
        return <UsageMeter {...common} used={ai.used} footer="Covered by BotCircuits on this plan" />;
    }
    return <UsageMeter
        {...common}
        used={ai.used}
        included={ai.included}
        ratio={ai.includedUsd ? ai.usedUsd / ai.includedUsd : null}
        footer={ai.usedUsd > ai.includedUsd
            ? `Above the included amount: ${ai.overageAmount}`
            : `Billed at cost above the included amount on the ${plan.displayName} plan`}
    />;
}

function Addons({ dashboardData, onChange, saving }) {
    const { plan, addons } = dashboardData;
    const [additionalSeats, setAdditionalSeats] = useState(addons.additionalSeats);
    const supportsSeats = plan.additionalSeatCents != null;
    const supportsByok = plan.byokAddonCents != null;

    return (
        <SectionCard title="Add-ons" description="Billed monthly with your plan, prorated when changed">
            {!supportsSeats && !supportsByok
                ? <p className="text-sm text-gray-500">Add-ons are available on paid plans.</p>
                : <div className="flex flex-col divide-y divide-gray-100">
                    {supportsSeats &&
                        <div className="flex flex-col gap-3 pb-4">
                            <div>
                                <p className="text-sm font-medium text-gray-900">Helpdesk seats</p>
                                <p className="text-xs text-gray-500">
                                    {plan.helpdeskSeatsIncluded} included · extra {formatCents(plan.additionalSeatCents)} / seat / month
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min={0}
                                    aria-label="Additional helpdesk seats"
                                    className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                    value={additionalSeats}
                                    onChange={e => setAdditionalSeats(Math.max(parseInt(e.target.value || '0', 10), 0))}
                                />
                                <span className="text-xs text-gray-500">extra seats</span>
                                <Button
                                    rootClass="ml-auto"
                                    text="Save"
                                    color="default"
                                    loading={saving}
                                    disabled={saving || additionalSeats === addons.additionalSeats}
                                    onClick={() => onChange({ additionalSeats })}
                                />
                            </div>
                        </div>
                    }
                    {supportsByok &&
                        <div className={supportsSeats ? 'pt-4' : ''}>
                            <Switch
                                title="Bring your own AI key"
                                subTitle={`Use your own AI provider key · ${formatCents(plan.byokAddonCents)} / month`}
                                enabled={addons.byok}
                                loading={saving}
                                onChange={byok => onChange({ byok })}
                            />
                        </div>
                    }
                </div>
            }
        </SectionCard>
    );
}

function UpcomingInvoice({ upcomingInvoice }) {
    return (
        <SectionCard
            title="Upcoming invoice"
            description={`Preview of the invoice billed on ${upcomingInvoice.nextPaymentDate}, usage so far included. It may change before then.`}
            action={<span className="text-lg font-semibold text-gray-900">{upcomingInvoice.amountDue}</span>}
            bodyClassName="overflow-x-auto"
        >
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        <th scope="col" className="px-5 py-3">Description</th>
                        <th scope="col" className="px-5 py-3">Quantity</th>
                        <th scope="col" className="px-5 py-3 text-right">Amount</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {upcomingInvoice.lines.map(line =>
                        <tr key={line.component}>
                            <td className="px-5 py-3 text-gray-700">{line.title}</td>
                            <td className="px-5 py-3 text-gray-500">{line.quantity != null ? line.quantity.toLocaleString('en-US') : '–'}</td>
                            <td className="px-5 py-3 text-right font-medium text-gray-900">{line.amount}</td>
                        </tr>
                    )}
                </tbody>
                <tfoot className="border-t border-gray-200">
                    {[['Subtotal', upcomingInvoice.subtotal], ['Total', upcomingInvoice.total], ['Amount due', upcomingInvoice.amountDue]].map(([title, amount], i) =>
                        <tr key={title}>
                            <td />
                            <td className={`px-5 py-2 ${i === 2 ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{title}</td>
                            <td className={`px-5 py-2 text-right ${i === 2 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{amount}</td>
                        </tr>
                    )}
                </tfoot>
            </table>
        </SectionCard>
    );
}

export default function Index() {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loadState, setLoadState] = useState({ loading: true, error: null, notFound: false });
    const [actionError, setActionError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [savingAddons, setSavingAddons] = useState(false);
    const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState(false);

    // the first load shows a loading state; refreshes after an action keep the page in place
    async function fetchData() {
        const { response, status } = await getDashboardResponse();
        if (status === 200) {
            setDashboardData(response);
            setLoadState({ loading: false, error: null, notFound: false });
        } else if (status === 404) {
            setLoadState({ loading: false, error: null, notFound: true });
        } else {
            setLoadState({ loading: false, error: 'Unable to load billing data. Please try again', notFound: false });
        }
    }

    const handleConfirmCancelSubscription = async confirmed => {
        setOpenCancelConfirmDialog(false);
        if (confirmed) {
            setActionError(null);
            setBusy(true);
            const { status } = await cancelSubscription();
            if (status !== 200) {
                setActionError('Unable to cancel the subscription. Please try again');
            }
            await fetchData();
            setBusy(false);
        }
    }

    const onClickResumeSubscription = async () => {
        setActionError(null);
        setBusy(true);
        const { status } = await resumeSubscription();
        if (status !== 200) {
            setActionError('Unable to keep the current plan. Please try again');
        }
        await fetchData();
        setBusy(false);
    }

    const onChangeAddons = async addons => {
        setActionError(null);
        setSavingAddons(true);
        const { status, response } = await updateAddons(addons);
        setSavingAddons(false);
        if (status === 400 && response === 'payment_method_not_found') {
            navigate(`${SUBSCRIPTION_BASE}/payment-methods?setup=1`);
            return;
        }
        if (status !== 200) {
            setActionError(status === 402 ? 'Payment failed. Please update your payment method' : (response || 'Unable to update add-ons'));
        }
        await fetchData();
    }

    const redirectToSelectPlans = () => navigate(`${SUBSCRIPTION_BASE}/plans`);

    useEffect(() => {
        fetchData();
    }, []);

    if (loadState.loading) {
        return <LoadingState text="Loading usage..." />;
    }
    if (loadState.notFound) {
        return (
            <SectionCard>
                <EmptyState
                    icon={HiOutlineSparkles}
                    title="No active subscription"
                    description="Choose a plan to start serving conversations."
                    action={<Button text="Choose a plan" onClick={redirectToSelectPlans} />}
                />
            </SectionCard>
        );
    }
    if (loadState.error || !dashboardData) {
        return (
            <Notice tone="danger" title={loadState.error || 'Unable to load billing data'}
                    action={<Button text="Retry" color="default" onClick={() => { setLoadState({ loading: true }); fetchData(); }} />} />
        );
    }

    const { pendingChange, legacyPlan } = dashboardData;

    return (
        <div className="flex flex-col gap-6">
            {openCancelConfirmDialog &&
                <ConfirmDialog
                    open={openCancelConfirmDialog}
                    onCompletion={handleConfirmCancelSubscription}
                    message={`Cancel your ${dashboardData.plan.displayName} subscription? You keep it until ${dashboardData.currentPeriod.end}, then your account moves to the Free plan.`}
                />
            }

            {actionError && <Notice tone="danger">{actionError}</Notice>}

            {legacyPlan &&
                <Notice tone="warning" title="You are on a retired plan"
                        action={<Button text="See plans" onClick={redirectToSelectPlans} />}>
                    Choose one of the current plans to get the new conversation and AI usage allowances.
                </Notice>
            }

            {pendingChange &&
                <Notice tone="info" title={pendingChange.plan ? `Plan change scheduled` : 'Subscription ending'}>
                    {pendingChange.plan
                        ? `Your plan changes to ${pendingChange.plan} on ${pendingChange.effectiveAt}.`
                        : `Your subscription ends on ${pendingChange.effectiveAt}.`}
                </Notice>
            }

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <PlanSummary
                    dashboardData={dashboardData}
                    busy={busy}
                    onChangePlan={redirectToSelectPlans}
                    onCancel={() => setOpenCancelConfirmDialog(true)}
                    onKeepPlan={onClickResumeSubscription}
                />
                {!legacyPlan && <ConversationsMeter dashboardData={dashboardData} />}
                {!legacyPlan && <AiUsageMeter dashboardData={dashboardData} />}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className={legacyPlan ? 'lg:col-span-3' : 'lg:col-span-2'}>
                    {dashboardData.upcomingInvoice
                        ? <UpcomingInvoice upcomingInvoice={dashboardData.upcomingInvoice} />
                        : <SectionCard title="Upcoming invoice">
                            <p className="text-sm text-gray-500">No upcoming invoice.</p>
                        </SectionCard>}
                </div>
                {!legacyPlan &&
                    <Addons
                        key={JSON.stringify(dashboardData.addons)}
                        dashboardData={dashboardData}
                        onChange={onChangeAddons}
                        saving={savingAddons}
                    />
                }
            </div>
        </div>
    );
}
